// hooks/useUnreadTeamMessages.js - Hook pour compter les messages d'équipe non lus
import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  onSnapshot, 
  orderBy,
  where 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';

const useUnreadTeamMessages = () => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastReadTimestamp, setLastReadTimestamp] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setCount(0);
      setLoading(false);
      return;
    }

    console.log("🎯 TEAM-MESSAGES: Initialisation hook pour user:", user.uid);

    // Récupérer le timestamp de dernière lecture depuis localStorage
    const storedTimestamp = localStorage.getItem(`team_messages_last_read_${user.uid}`);
    const lastRead = storedTimestamp ? new Date(storedTimestamp) : new Date(0);
    setLastReadTimestamp(lastRead);

    console.log("🎯 TEAM-MESSAGES: Dernière lecture:", lastRead.toLocaleString('fr-FR'));

    // Écouter les messages d'équipe en temps réel
    const messagesQuery = query(
      collection(db, 'team_messages'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      console.log("🎯 TEAM-MESSAGES: Snapshot reçu avec", snapshot.size, "messages");
      
      let unreadCount = 0;
      const now = new Date();

      snapshot.docs.forEach(doc => {
        const message = doc.data();
        const messageDate = message.createdAt?.toDate() || new Date(0);
        const isFromCurrentUser = message.createdBy === user.uid;
        const isUnread = messageDate > lastRead;

        console.log(`📨 TEAM-MESSAGE ${doc.id}:`, {
          text: message.text?.slice(0, 30) + "...",
          createdBy: message.createdBy,
          createdByName: message.createdByName,
          date: messageDate.toLocaleString('fr-FR'),
          isFromCurrentUser,
          isUnread,
          dossierTag: message.dossierTag || 'général'
        });

        // Compter seulement les messages des autres utilisateurs non lus
        if (!isFromCurrentUser && isUnread) {
          unreadCount++;
          console.log("✅ TEAM-MESSAGES: Message compté comme non lu");
        } else if (isFromCurrentUser) {
          console.log("🚫 TEAM-MESSAGES: Message exclu (créé par l'utilisateur)");
        } else if (!isUnread) {
          console.log("🚫 TEAM-MESSAGES: Message exclu (déjà lu)");
        }
      });

      console.log("🎯 TEAM-MESSAGES: Comptage final:", unreadCount);
      setCount(unreadCount);
      setLoading(false);
    }, (error) => {
      console.error("❌ TEAM-MESSAGES: Erreur snapshot:", error);
      setLoading(false);
    });

    return () => {
      console.log("🎯 TEAM-MESSAGES: Nettoyage hook");
      unsubscribe();
    };
  }, [user?.uid]);

  // Fonction pour marquer comme lu (quand l'utilisateur visite la page)
  const markAsRead = () => {
    if (!user?.uid) return;
    
    const now = new Date();
    localStorage.setItem(`team_messages_last_read_${user.uid}`, now.toISOString());
    setLastReadTimestamp(now);
    setCount(0);
    
    console.log("✅ TEAM-MESSAGES: Marqué comme lu à", now.toLocaleString('fr-FR'));
  };

  return { 
    count, 
    loading, 
    markAsRead,
    lastReadTimestamp 
  };
};

export default useUnreadTeamMessages;